// Spotify Web API wrapper
import { getAccessToken } from './spotifyAuth';

const API_BASE = 'https://api.spotify.com/v1';

// Make authenticated API request
async function apiRequest(endpoint, options = {}) {
    const token = await getAccessToken();
    if (!token) {
        throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        },
    });

    if (response.status === 204) {
        return null; // No content response
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || `API request failed: ${response.status}`);
    }

    return response.json();
}

// Get current user profile
export async function getCurrentUser() {
    return apiRequest('/me');
}

// Search for tracks
export async function searchTracks(query, limit = 10) {
    const params = new URLSearchParams({
        q: query,
        type: 'track',
        limit: limit.toString(),
        market: 'US',
    });

    const data = await apiRequest(`/search?${params.toString()}`);
    return data.tracks.items;
}

// Add item to playback queue
export async function addToQueue(trackUri) {
    const params = new URLSearchParams({
        uri: trackUri,
    });

    try {
        const token = await getAccessToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE}/me/player/queue?${params.toString()}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.status === 204) {
            return true; // Success
        }

        if (response.status === 404) {
            throw new Error('No active Spotify player found. Start playing a song on Spotify first, then try again!');
        }

        if (response.status === 403) {
            throw new Error('Spotify Premium is required to add songs to queue.');
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error?.message || `Failed to add to queue (${response.status})`);
        }

        return true;
    } catch (error) {
        // Make error messages more user-friendly
        if (error.message.includes('No active device')) {
            throw new Error('No active Spotify player found. Start playing a song on Spotify first, then try again!');
        }
        throw error;
    }
}

// Get currently playing track
export async function getCurrentlyPlaying() {
    try {
        const data = await apiRequest('/me/player/currently-playing');
        return data;
    } catch (error) {
        // No active device or nothing playing
        return null;
    }
}

// Get user's playback queue (currently playing + upcoming)
export async function getQueue() {
    try {
        const data = await apiRequest('/me/player/queue');
        return {
            currentlyPlaying: data.currently_playing,
            queue: data.queue || [],
        };
    } catch (error) {
        console.error('Failed to get queue:', error);
        return {
            currentlyPlaying: null,
            queue: [],
        };
    }
}

// Check if user is admin (Divyesh)
export function isAdminUser(user) {
    if (!user) return false;

    const email = user.email?.toLowerCase() || '';
    const displayName = user.display_name?.toLowerCase() || '';

    return (
        email === 'divyesh.thirukonda@gmail.com' ||
        displayName.includes('divyesh')
    );
}

// Get current user's playlists
export async function getUserPlaylists(limit = 50) {
    const params = new URLSearchParams({
        limit: limit.toString(),
    });

    const data = await apiRequest(`/me/playlists?${params.toString()}`);
    return data.items;
}

// Get tracks from a specific playlist
export async function getPlaylistTracks(playlistId, limit = 50) {
    const params = new URLSearchParams({
        limit: limit.toString(),
        market: 'US',
    });

    const data = await apiRequest(`/playlists/${playlistId}/tracks?${params.toString()}`);
    return data.items
        .filter((item) => item.track && item.track.type === 'track')
        .map((item) => item.track);
}

// Get random songs from across all public playlists for "Want Ideas?" feature
export async function getRandomSongs(count = 4) {
    const playlists = await getUserPlaylists();

    // Filter to only public playlists with at least some tracks
    const publicPlaylists = playlists.filter(
        (p) => p.public && p.tracks.total > 0
    );

    if (publicPlaylists.length === 0) {
        return [];
    }

    // Pick a few random playlists to sample from (to avoid too many API calls)
    const shuffledPlaylists = publicPlaylists.sort(() => Math.random() - 0.5);
    const playlistsToSample = shuffledPlaylists.slice(0, Math.min(3, shuffledPlaylists.length));

    // Fetch tracks from selected playlists
    const allTracks = [];
    for (const playlist of playlistsToSample) {
        try {
            const tracks = await getPlaylistTracks(playlist.id, 50);
            allTracks.push(...tracks);
        } catch (err) {
            console.error(`Failed to fetch tracks from playlist ${playlist.id}:`, err);
        }
    }

    if (allTracks.length === 0) {
        return [];
    }

    // Remove duplicates by track ID
    const uniqueTracks = Array.from(
        new Map(allTracks.map((track) => [track.id, track])).values()
    );

    // Shuffle and pick random ones
    const shuffledTracks = uniqueTracks.sort(() => Math.random() - 0.5);
    return shuffledTracks.slice(0, count);
}
